/**
 * Núcleo matemático de la prueba COP.
 * Port del script Python Centro_de_presion.py (COP_dos_plataformas_serial.py).
 *
 * IMPORTANTE:
 * La trama COP esperada es:
 * P1_S1,P1_S2,P1_S3,P1_S4,P2_S1,P2_S2,P2_S3,P2_S4,TIEMPO_MS
 *
 * El mapeo es el mismo del Python:
 * P1_AI=3, P1_AD=1, P1_PI=2, P1_PD=0
 * P2_AI=4, P2_AD=5, P2_PI=7, P2_PD=6
 *
 * GANANCIA_CANALES = 1 significa que los 8 canales ya están calibrados
 * en la unidad de fuerza configurada (N). Si el hardware realmente entrega
 * kg, cambia GANANCIA_CANALES a 9.80665.
 */

export const ACELERACION_GRAVEDAD_COP = 9.80665;

export const MAPEO_CANALES = {
    P1_AI: 3,
    P1_AD: 1,
    P1_PI: 2,
    P1_PD: 0,
    P2_AI: 4,
    P2_AD: 5,
    P2_PI: 7,
    P2_PD: 6,
};

export const INDICES_P1 = [
    MAPEO_CANALES.P1_AI,
    MAPEO_CANALES.P1_AD,
    MAPEO_CANALES.P1_PI,
    MAPEO_CANALES.P1_PD,
];

export const INDICES_P2 = [
    MAPEO_CANALES.P2_AI,
    MAPEO_CANALES.P2_AD,
    MAPEO_CANALES.P2_PI,
    MAPEO_CANALES.P2_PD,
];

export const ANCHO_P1_MM = 300;
export const LARGO_P1_MM = 480;
export const ANCHO_P2_MM = 300;
export const LARGO_P2_MM = 480;
export const SEPARACION_BORDES_MM = 20;
export const DESFASE_Y_P2_MM = 0;

export const UMBRAL_MINIMO_FUERZA = 0.2;
export const FACTOR_RUIDO_UMBRAL = 6;
export const DURACION_TARA_S = 5;
export const MUESTRAS_MINIMAS_TARA = 20;
export const CARGA_MAXIMA_DURANTE_TARA = 2;
export const GANANCIA_CANALES = Array(8).fill(1);

export const NIVEL_ELIPSE = 0.95;
export const MIN_MUESTRAS_ELIPSE = 20;
export const PLATAFORMA_IZQUIERDA = "P1";

export const COORD_P1_LOCAL = [
    [-ANCHO_P1_MM / 2, +LARGO_P1_MM / 2],
    [+ANCHO_P1_MM / 2, +LARGO_P1_MM / 2],
    [-ANCHO_P1_MM / 2, -LARGO_P1_MM / 2],
    [+ANCHO_P1_MM / 2, -LARGO_P1_MM / 2],
];

export const COORD_P2_LOCAL = [
    [-ANCHO_P2_MM / 2, +LARGO_P2_MM / 2],
    [+ANCHO_P2_MM / 2, +LARGO_P2_MM / 2],
    [-ANCHO_P2_MM / 2, -LARGO_P2_MM / 2],
    [+ANCHO_P2_MM / 2, -LARGO_P2_MM / 2],
];

export const CENTRO_P1_GLOBAL = [
    -(SEPARACION_BORDES_MM / 2 + ANCHO_P1_MM / 2),
    0,
];

export const CENTRO_P2_GLOBAL = [
    +(SEPARACION_BORDES_MM / 2 + ANCHO_P2_MM / 2),
    DESFASE_Y_P2_MM,
];

export const numeroFinito = (valor) => {
    const n = Number(valor);
    return Number.isFinite(n) ? n : null;
};

export const desviacionMuestral = (valores) => {
    const validos = valores.filter(Number.isFinite);
    if (validos.length < 2) return 0;

    const media = validos.reduce((a, b) => a + b, 0) / validos.length;
    const suma = validos.reduce((acc, valor) => acc + (valor - media) ** 2, 0);
    return Math.sqrt(suma / (validos.length - 1));
};

export const mediaPicoDesviacion = (valores) => {
    const validos = valores.filter(Number.isFinite);
    if (!validos.length) {
        return { media: NaN, pico: NaN, desviacion: NaN };
    }

    const media = validos.reduce((a, b) => a + b, 0) / validos.length;
    const pico = Math.max(...validos);
    const desviacion =
        validos.length >= 2 ? desviacionMuestral(validos) : NaN;

    return { media, pico, desviacion };
};

export const asimetriaIzquierdaDerecha = (izquierda, derecha) => {
    if (!Number.isFinite(izquierda) || !Number.isFinite(derecha)) {
        return NaN;
    }

    const denominador = (Math.abs(izquierda) + Math.abs(derecha)) / 2;
    if (denominador <= Number.EPSILON) return NaN;

    return (
        (100 * (izquierda - derecha)) /
        denominador
    );
};

export const calcularCopPlataforma = (fuerzas, coordenadas, umbral) => {
    const fuerzaTotal = fuerzas.reduce((a, b) => a + b, 0);

    if (
        !fuerzas.length ||
        !fuerzas.every(Number.isFinite) ||
        fuerzaTotal <= umbral
    ) {
        return {
            cop: [NaN, NaN],
            fuerzaTotal,
            valido: false,
        };
    }

    let copX = 0;
    let copY = 0;

    fuerzas.forEach((fuerza, index) => {
        copX += fuerza * coordenadas[index][0];
        copY += fuerza * coordenadas[index][1];
    });

    copX /= fuerzaTotal;
    copY /= fuerzaTotal;

    return {
        cop: [copX, copY],
        fuerzaTotal,
        valido: true,
    };
};

export const procesarMuestraCOP = (muestraCruda, offsets, umbrales) => {
    if (
        !Array.isArray(muestraCruda) ||
        muestraCruda.length !== 8 ||
        !muestraCruda.every(Number.isFinite)
    ) {
        throw new Error("La muestra COP debe contener exactamente 8 valores.");
    }

    const fuerzasCorregidas = muestraCruda.map(
        (valor, index) => (valor - offsets[index]) * GANANCIA_CANALES[index]
    );

    const fuerzasP1 = INDICES_P1.map((index) => fuerzasCorregidas[index]);
    const fuerzasP2 = INDICES_P2.map((index) => fuerzasCorregidas[index]);

    const p1 = calcularCopPlataforma(
        fuerzasP1,
        COORD_P1_LOCAL,
        umbrales[0]
    );

    const p2 = calcularCopPlataforma(
        fuerzasP2,
        COORD_P2_LOCAL,
        umbrales[1]
    );

    const copP1Global = p1.valido
        ? [
              p1.cop[0] + CENTRO_P1_GLOBAL[0],
              p1.cop[1] + CENTRO_P1_GLOBAL[1],
          ]
        : [NaN, NaN];

    const copP2Global = p2.valido
        ? [
              p2.cop[0] + CENTRO_P2_GLOBAL[0],
              p2.cop[1] + CENTRO_P2_GLOBAL[1],
          ]
        : [NaN, NaN];

    let copTotal = [NaN, NaN];

    if (p1.valido && p2.valido) {
        copTotal = [
            (p1.fuerzaTotal * copP1Global[0] +
                p2.fuerzaTotal * copP2Global[0]) /
                (p1.fuerzaTotal + p2.fuerzaTotal),
            (p1.fuerzaTotal * copP1Global[1] +
                p2.fuerzaTotal * copP2Global[1]) /
                (p1.fuerzaTotal + p2.fuerzaTotal),
        ];
    } else if (p1.valido) {
        copTotal = [...copP1Global];
    } else if (p2.valido) {
        copTotal = [...copP2Global];
    }

    return {
        muestraCruda: [...muestraCruda],
        valoresRecibidosP1: INDICES_P1.map((i) => muestraCruda[i]),
        valoresRecibidosP2: INDICES_P2.map((i) => muestraCruda[i]),
        fuerzasCorregidas,
        fzP1: p1.fuerzaTotal,
        fzP2: p2.fuerzaTotal,
        fzTotal: p1.fuerzaTotal + p2.fuerzaTotal,
        copP1Local: p1.cop,
        copP2Local: p2.cop,
        copP1Global,
        copP2Global,
        copTotal,
        validoP1: p1.valido,
        validoP2: p2.valido,
    };
};

export const resumenSway = (historial) => {
    const validos = historial.filter(
        (punto) =>
            Number.isFinite(punto?.tiempoMs) &&
            Number.isFinite(punto?.x) &&
            Number.isFinite(punto?.y)
    );

    if (!validos.length) {
        return {
            apLengthCm: NaN,
            apRangeCm: NaN,
            apVelocityCmS: NaN,
            mlLengthCm: NaN,
            mlRangeCm: NaN,
            mlVelocityCmS: NaN,
            swayLengthCm: NaN,
            swayRangeCm: NaN,
            swayVelocityCmS: NaN,
        };
    }

    const mlValues = validos.map((p) => p.x);
    const apValues = validos.map((p) => p.y);

    const mlRangeCm =
        (Math.max(...mlValues) - Math.min(...mlValues)) / 10;
    const apRangeCm =
        (Math.max(...apValues) - Math.min(...apValues)) / 10;

    let apLengthCm = 0;
    let mlLengthCm = 0;
    let swayLengthCm = 0;
    let duracionActivaS = 0;

    for (let i = 1; i < historial.length; i += 1) {
        const anterior = historial[i - 1];
        const actual = historial[i];

        const validosSegmento =
            Number.isFinite(anterior?.tiempoMs) &&
            Number.isFinite(actual?.tiempoMs) &&
            Number.isFinite(anterior?.x) &&
            Number.isFinite(anterior?.y) &&
            Number.isFinite(actual?.x) &&
            Number.isFinite(actual?.y) &&
            actual.tiempoMs > anterior.tiempoMs;

        if (!validosSegmento) continue;

        const deltaT = actual.tiempoMs - anterior.tiempoMs;
        const deltaMl = actual.x - anterior.x;
        const deltaAp = actual.y - anterior.y;

        apLengthCm += Math.abs(deltaAp) / 10;
        mlLengthCm += Math.abs(deltaMl) / 10;
        swayLengthCm += Math.hypot(deltaMl, deltaAp) / 10;
        duracionActivaS += deltaT / 1000;
    }

    const swayRangeCm = Math.hypot(apRangeCm, mlRangeCm);

    return {
        apLengthCm,
        apRangeCm,
        apVelocityCmS:
            duracionActivaS > Number.EPSILON
                ? apLengthCm / duracionActivaS
                : NaN,
        mlLengthCm,
        mlRangeCm,
        mlVelocityCmS:
            duracionActivaS > Number.EPSILON
                ? mlLengthCm / duracionActivaS
                : NaN,
        swayLengthCm,
        swayRangeCm,
        swayVelocityCmS:
            duracionActivaS > Number.EPSILON
                ? swayLengthCm / duracionActivaS
                : NaN,
    };
};

export const calcularMetricasCOP = (historialCOP) => {
    const fuerzasP1 = historialCOP.map((r) => r.fzP1);
    const fuerzasP2 = historialCOP.map((r) => r.fzP2);
    const fuerzasTotal = historialCOP.map((r) => r.fzTotal);

    const izquierda = PLATAFORMA_IZQUIERDA === "P1" ? fuerzasP1 : fuerzasP2;
    const derecha = PLATAFORMA_IZQUIERDA === "P1" ? fuerzasP2 : fuerzasP1;

    const total = mediaPicoDesviacion(fuerzasTotal);
    const izq = mediaPicoDesviacion(izquierda);
    const der = mediaPicoDesviacion(derecha);

    const historialP1 = historialCOP.map((r) => ({
        tiempoMs: r.tiempoMs,
        x: r.copP1Local?.[0],
        y: r.copP1Local?.[1],
    }));

    const historialP2 = historialCOP.map((r) => ({
        tiempoMs: r.tiempoMs,
        x: r.copP2Local?.[0],
        y: r.copP2Local?.[1],
    }));

    const swayIzquierda = PLATAFORMA_IZQUIERDA === "P1"
        ? resumenSway(historialP1)
        : resumenSway(historialP2);

    const swayDerecha = PLATAFORMA_IZQUIERDA === "P1"
        ? resumenSway(historialP2)
        : resumenSway(historialP1);

    const parametros = [];

    const agregar = (nombre, unidad, valor, categoria = "Fuerza") => {
        parametros.push({
            key: `${categoria}-${nombre}`,
            nombre,
            valor,
            u: unidad,
            categoria,
        });
    };

    agregar("Fuerza promedio", "N", total.media);
    agregar("Fuerza pico", "N", total.pico);
    agregar("Fuerza promedio izquierda", "N", izq.media);
    agregar("Fuerza promedio derecha", "N", der.media);
    agregar("Fuerza pico izquierda", "N", izq.pico);
    agregar("Fuerza pico derecha", "N", der.pico);
    agregar(
        "Desviación estándar de la fuerza izquierda",
        "N",
        izq.desviacion
    );
    agregar(
        "Desviación estándar de la fuerza derecha",
        "N",
        der.desviacion
    );
    agregar(
        "Desviación estándar de la fuerza total",
        "N",
        total.desviacion
    );
    agregar(
        "Asimetría I|D de la fuerza promedio",
        "%",
        asimetriaIzquierdaDerecha(izq.media, der.media)
    );
    agregar(
        "Asimetría I|D de la fuerza pico",
        "%",
        asimetriaIzquierdaDerecha(izq.pico, der.pico)
    );

    const agregarMetricasSway = (lado, sway) => {
        agregar(
            `${lado} - Longitud de balanceo AP`,
            "cm",
            sway.apLengthCm,
            "COP"
        );
        agregar(
            `${lado} - Rango de balanceo AP`,
            "cm",
            sway.apRangeCm,
            "COP"
        );
        agregar(
            `${lado} - Velocidad media de balanceo AP`,
            "cm/s",
            sway.apVelocityCmS,
            "COP"
        );
        agregar(
            `${lado} - Longitud de balanceo ML`,
            "cm",
            sway.mlLengthCm,
            "COP"
        );
        agregar(
            `${lado} - Rango de balanceo ML`,
            "cm",
            sway.mlRangeCm,
            "COP"
        );
        agregar(
            `${lado} - Velocidad media de balanceo ML`,
            "cm/s",
            sway.mlVelocityCmS,
            "COP"
        );
        agregar(
            `${lado} - Longitud total de balanceo`,
            "cm",
            sway.swayLengthCm,
            "COP"
        );
        agregar(
            `${lado} - Rango total de balanceo`,
            "cm",
            sway.swayRangeCm,
            "COP"
        );
        agregar(
            `${lado} - Velocidad media total de balanceo`,
            "cm/s",
            sway.swayVelocityCmS,
            "COP"
        );
    };

    agregarMetricasSway("Izquierda", swayIzquierda);
    agregarMetricasSway("Derecha", swayDerecha);

    return parametros;
};

export const calcularTara = (muestras) => {
    if (muestras.length < MUESTRAS_MINIMAS_TARA) {
        throw new Error(
            `La tara requiere al menos ${MUESTRAS_MINIMAS_TARA} muestras; se recibieron ${muestras.length}.`
        );
    }

    const offsets = Array(8).fill(0);

    for (let index = 0; index < 8; index += 1) {
        offsets[index] =
            muestras.reduce((acc, muestra) => acc + muestra[index], 0) /
            muestras.length;
    }

    const cargaMediaP1 = INDICES_P1.reduce(
        (acc, index) => acc + offsets[index],
        0
    );

    const cargaMediaP2 = INDICES_P2.reduce(
        (acc, index) => acc + offsets[index],
        0
    );

    if (
        Math.abs(cargaMediaP1) > CARGA_MAXIMA_DURANTE_TARA ||
        Math.abs(cargaMediaP2) > CARGA_MAXIMA_DURANTE_TARA
    ) {
        throw new Error(
            `La tara detectó carga. P1=${cargaMediaP1.toFixed(
                2
            )}; P2=${cargaMediaP2.toFixed(
                2
            )}. Retira todo el peso de las plataformas.`
        );
    }

    const corregidas = muestras.map((muestra) =>
        muestra.map(
            (valor, index) =>
                (valor - offsets[index]) * GANANCIA_CANALES[index]
        )
    );

    const ruidoP1 = desviacionMuestral(
        corregidas.map((muestra) =>
            INDICES_P1.reduce((acc, index) => acc + muestra[index], 0)
        )
    );

    const ruidoP2 = desviacionMuestral(
        corregidas.map((muestra) =>
            INDICES_P2.reduce((acc, index) => acc + muestra[index], 0)
        )
    );

    return {
        offsets,
        umbrales: [
            Math.max(UMBRAL_MINIMO_FUERZA, FACTOR_RUIDO_UMBRAL * ruidoP1),
            Math.max(UMBRAL_MINIMO_FUERZA, FACTOR_RUIDO_UMBRAL * ruidoP2),
        ],
    };
};

/**
 * Cálculo de parámetros COP sobre un historial guardado
 * (`prueba.datos`, antes `copHistorial` en captura).
 * Mismo contrato que runCMJ/runNordic/runDynamo.
 */
export async function runCOP(historialCOP) {
    const lista = Array.isArray(historialCOP) ? historialCOP : [];
    if (!lista.length) {
        throw new Error("Sin datos COP para calcular.");
    }
    return {
        fases: [],
        parametros: calcularMetricasCOP(lista),
    };
}

/** Extrae el historial COP guardado de una prueba (rep ya resuelta). */
export function copHistorialDePrueba(prueba) {
    const datos = prueba?.datos;
    if (Array.isArray(datos) && datos.length > 0 && datos[0]?.copTotal) {
        return datos;
    }
    return [];
}

/** ¿La prueba guardada contiene datos COP para visualizar? */
export function pruebaTieneCOP(prueba) {
    return copHistorialDePrueba(prueba).length > 0;
}
